const units = ['zero','one','two','three','four','five','six','seven','eight','nine'];
const teens = ['ten','eleven','twelve','thirteen','fourteen','fifteen','sixteen','seventeen','eighteen','nineteen'];
const tens = ['twenty','thirty','forty','fifty','sixty','seventy','eighty','ninety'];
export const validQuestionNumber = value => /^\d{1,4}$/.test(String(value ?? '').trim()) && Number(value)>0 ? Number(value) : null;
function underThousand(words) {
    let total=0; let tokens=[...words];
    if(tokens.length>=2 && units.indexOf(tokens[0])>0 && tokens[1]==='hundred') { total=units.indexOf(tokens[0])*100;tokens=tokens.slice(2); }
    if(!tokens.length) return total;
    if(tokens.length===1 && units.includes(tokens[0])) return total+units.indexOf(tokens[0]);
    if(tokens.length===1 && teens.includes(tokens[0])) return total+10+teens.indexOf(tokens[0]);
    if(tokens.length<=2 && tens.includes(tokens[0]) && (tokens.length===1 || units.indexOf(tokens[1])>0)) return total+(tens.indexOf(tokens[0])+2)*10+(tokens.length===2?units.indexOf(tokens[1]):0);
    return null;
}
export function spokenNumber(value) {
    const text=String(value).toLowerCase().trim().replace(/-/g,' ').replace(/\b(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)(one|two|three|four|five|six|seven|eight|nine)\b/g,'$1 $2');
    if(/^\d+$/.test(text)) return validQuestionNumber(text);
    const words=text.split(/\s+/).filter(w=>w!=='and');
    if(words.length>1 && words.every(w=>units.includes(w) || w==='oh')) return validQuestionNumber(words.map(w=>w==='oh'?0:units.indexOf(w)).join(''));
    const split=words.indexOf('thousand'); let result;
    if(split!==-1) {
        if(split!==1 || units.indexOf(words[0])<1 || words.lastIndexOf('thousand')!==split) return null;
        const rest=underThousand(words.slice(2)); if(rest===null) return null;
        result=units.indexOf(words[0])*1000+rest;
    } else result=underThousand(words);
    return result===null?null:validQuestionNumber(result);
}
// Recognition often adds sentence punctuation after the brand name, or hears
// "Instil" as "install". Keep aliases explicit and require the full wake phrase.
export function wakeCommand(transcript) {
    const text=String(transcript || '').toLowerCase();
    const matches=[...text.matchAll(/\bhey[\s,!.:;]+(?:instill?|install|in[\s-]+still)\b[\s,!.:;]*/g)];
    if(!matches.length)return null;
    const last=matches[matches.length-1];
    return text.slice(last.index+last[0].length).trim();
}
function numberText(transcript) {
    let text=String(transcript ?? '').toLowerCase().trim().replace(/[.!?,;]+$/g,'').replace(/\s+/g,' ');
    text=text.replace(/^(?:(?:can|could|would|will) you\s+)?(?:please\s+)?/,'');
    text=text.replace(/^(?:open|play|show(?: me)?|find|search(?: for)?|go to|take me to)\s+(?:the\s+)?/,'').replace(/^please\s+/,'');
    text=text.replace(/(?:[,.]?\s+(?:please|thanks|thank you))+$/,'');
    text=text.replace(/^(?:question|q)\s*(?:(?:number|no\.?)\s*)?[#:]?\s*/,'').replace(/^number\s*/,'').replace(/^#/,'');
    if(/^\d{1,3},\d{3}$/.test(text))text=text.replace(',','');
    if(/^\d(?:\s\d){1,3}$/.test(text))text=text.replace(/\s/g,'');
    return text.trim();
}
export function incompleteQuestionCommand(command) {
    return !numberText(command) || /^(?:(?:can|could|would|will) you\s+)?(?:please\s+)?(?:open|play|show(?: me)?|find|search(?: for)?|go to|take me to)(?: the)?$/.test(command.trim().toLowerCase());
}
export function mergeVoiceFragments(carried, text) {
    // A fresh explicit request replaces an unfinished prefix; a number fragment
    // continues it ("question twenty" + "four").
    if(/^(?:please\s+)?(?:question|number|q\b|open|play|show|find|search|go to|take me to|can you|could you|would you)\b/i.test(text.trim()))return text;
    return `${carried} ${text}`.trim();
}
export function parseQuestionCommand(transcript,{requireWake=true}={}) {
    const command=wakeCommand(transcript);
    if(requireWake && command===null)return null;
    return spokenNumber(numberText(command ?? transcript));
}

// Consider a small set of browser hypotheses, including alternatives for the
// brand name. Never replace a valid primary number with a different number.
export function recognitionCommand(results, {armed=false, carried=''}={}) {
    if(!results.length)return null;
    let candidates=[{text:'',rank:0}];
    for(let i=Math.max(0,results.length-8);i<results.length;i++) {
        const result=results[i];
        const next=[];
        for(const candidate of candidates) {
            for(let j=0;j<Math.min(result.length || 1,3);j++) {
                const words=result[j]?.transcript?.trim();
                if(words)next.push({text:`${candidate.text} ${words}`.trim().slice(-600),rank:candidate.rank+j});
            }
        }
        if(next.length)candidates=next.sort((a,b)=>a.rank-b.rank).slice(0,6);
    }
    const recognized=candidates.map(({text})=>{
        const wake=wakeCommand(text);
        const command=wake!==null ? wake : armed ? mergeVoiceFragments(carried,text) : null;
        return {text,command,number:command===null?null:parseQuestionCommand(command,{requireWake:false})};
    });
    // If the main transcript already contains the wake phrase, keep its exact
    // request, including invalid/ambiguous numbers. Alternatives must not turn
    // "12 or 24" (or "minus one") into an unintended question selection.
    const valid=recognized.filter(item=>item.number!==null);
    const numbers=new Set(valid.map(item=>item.number));
    const primary=recognized[0];
    const options=[...numbers];
    if(primary.command!==null) {
        // Never turn an explicitly invalid/ambiguous request into a guess.
        const invalid=/\bor\b|\bminus\b|\bnegative\b|\bpoint\b|-\d|\d\.\d/.test(primary.command);
        if(primary.number!==null)return {...primary,options,ambiguous:numbers.size>1};
        if(!invalid && primary.command && valid.length)return {...valid[0],options,needsConfirmation:true,ambiguous:numbers.size>1,text:primary.text};
        return primary;
    }
    if(numbers.size===1)return {...valid[0],options};
    const first=recognized.find(item=>item.command!==null);
    return first ? {...first,options,ambiguous:numbers.size>1} : null;
}
// Explicit admin numbers are permanent. Legacy numbers are current-library labels,
// assigned in creation order and independent of search, filters, and pagination.
export function numberQuestions(questions) {
    const ordered=[...questions].sort((a,b)=>{
        const time=q=>q.createdAt?.toMillis?.() || (q.createdAt?.seconds ? q.createdAt.seconds*1000 : 0);
        return time(a)-time(b) || String(a.id).localeCompare(String(b.id));
    });
    const used=new Set(ordered.map(q=>validQuestionNumber(q.questionNumber)).filter(Boolean));
    let next=1;
    return ordered.map(q=>{
        const explicit=validQuestionNumber(q.questionNumber);
        if(explicit) return {...q,practiceNumber:explicit,numberIsAssigned:true};
        while(used.has(next)) next++;
        const assigned=next++;used.add(assigned);
        return {...q,practiceNumber:assigned,numberIsAssigned:false};
    });
}
export function findNumberedQuestion(questions,number) {
    const matches=questions.filter(q=>validQuestionNumber(q.practiceNumber ?? q.questionNumber)===number);
    return matches.length===1?{question:matches[0]}:{error:matches.length?'More than one question has this number. Ask your tutor to give each question a unique number.':`Question ${number} isn’t available in this subject. Check the number shown on its card.`};
}
